import React, { useEffect, useMemo, useState } from 'react'
import { createRoot } from 'react-dom/client'
import { MsalProvider } from '@azure/msal-react'
import { EventType } from '@azure/msal-browser'
import { ThemeProvider, CssBaseline } from '@mui/material'
import { createMsalInstance } from './msal.js'
import { loadAuthConfig, E2E_AUTH_CONFIG } from './authConfig.js'
import App from './App.jsx'
import { makeTheme } from './theme.js'
import { forcePlainTextCopy } from './copyPlainText.js'
import { installExternalLinkWindows } from './externalLinks.js'

const COLOR_MODE_KEY = 'cxhub.colorMode'

function initialMode() {
  const saved = localStorage.getItem(COLOR_MODE_KEY)
  if (saved === 'light' || saved === 'dark') return saved
  return window.matchMedia?.('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'
}

// Owns the color mode (persisted to localStorage) and provides the toggle to App.
function Root() {
  const [mode, setMode] = useState(initialMode)
  const theme = useMemo(() => makeTheme(mode), [mode])
  const toggleColorMode = () => {
    setMode((m) => {
      const next = m === 'dark' ? 'light' : 'dark'
      localStorage.setItem(COLOR_MODE_KEY, next)
      return next
    })
  }

  // App-wide: strip styled-ancestor background/color from every native copy
  // (Ctrl+C / right-click Copy), so pasting message-bubble text or any other
  // styled DOM content elsewhere always comes out as plain black text.
  useEffect(() => {
    function handleCopy(event) {
      forcePlainTextCopy(event, window.getSelection()?.toString())
    }
    document.addEventListener('copy', handleCopy)
    return () => document.removeEventListener('copy', handleCopy)
  }, [])

  // App-wide: a plain left click (or Enter) on a link to another origin opens
  // a new browser window instead of a tab (externalLinks.js, 2026-10-22).
  useEffect(() => installExternalLinkWindows(document, window), [])

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <App colorMode={mode} onToggleColorMode={toggleColorMode} />
    </ThemeProvider>
  )
}

// Which Entra app to sign in to comes from the server (authConfig.js). The
// Playwright build never signs in, so it skips the request and uses
// placeholders. A dev build may name a fallback with VITE_ENTRA_CLIENT_ID and
// VITE_ENTRA_TENANT_ID, used only when the local server runs with
// AUTH_DISABLED=true and so has no app to name. A production build never
// falls back: it signs in to the app its own server validates, or not at all.
function authConfigSource() {
  if (import.meta.env.VITE_E2E === 'true') return Promise.resolve(E2E_AUTH_CONFIG)
  const devId = import.meta.env.DEV ? import.meta.env.VITE_ENTRA_CLIENT_ID : null
  const devTenant = import.meta.env.DEV ? import.meta.env.VITE_ENTRA_TENANT_ID : null
  const fallback = devId && devTenant ? { entraClientId: devId, entraTenantId: devTenant } : null
  return loadAuthConfig({ fallback })
}

// Shown when the sign-in identifiers cannot be loaded. Plain DOM, no MUI: the
// app has not rendered and nothing else on the page can be trusted to.
function renderBootError(err) {
  console.error('CXHub could not load its sign-in configuration', err)
  const root = document.getElementById('root')
  const box = document.createElement('div')
  box.style.cssText = 'font-family: system-ui, sans-serif; padding: 24px; max-width: 560px'
  const title = document.createElement('h2')
  title.textContent = 'CXHub could not start sign-in'
  const text = document.createElement('p')
  text.textContent = 'The sign-in settings did not load from the server. Reload the page. If this keeps happening, tell an admin.'
  const button = document.createElement('button')
  button.textContent = 'Reload'
  button.onclick = () => window.location.reload()
  box.append(title, text, button)
  root.replaceChildren(box)
}

// MSAL v3 must be initialized before use; then process any redirect response
// and set the active account. Wrapped in an async bootstrap (no top-level await).
async function bootstrap() {
  let authConfig
  try {
    authConfig = await authConfigSource()
  } catch (err) {
    renderBootError(err)
    return
  }
  const msalInstance = createMsalInstance(authConfig)
  await msalInstance.initialize()
  await msalInstance.handleRedirectPromise()

  const accounts = msalInstance.getAllAccounts()
  if (accounts.length && !msalInstance.getActiveAccount()) {
    msalInstance.setActiveAccount(accounts[0])
  }
  msalInstance.addEventCallback((event) => {
    if (event.eventType === EventType.LOGIN_SUCCESS && event.payload?.account) {
      msalInstance.setActiveAccount(event.payload.account)
    }
  })

  createRoot(document.getElementById('root')).render(
    <React.StrictMode>
      <MsalProvider instance={msalInstance}>
        <Root />
      </MsalProvider>
    </React.StrictMode>
  )
}

bootstrap()
